/**
 * The class Counter provides a "get" method which returns the count of a given item in an array of items of
 * type T, or zero if the given item is not present.
 *
 * The hash function provided is used to convert the items in the array, as well as any argument provided to
 * "get", to strings in a consistent manner. This does not need to be a literal hash function; for a case
 * insensitive count of string items one could use the lowercase of the string for the hash, for example.
 */

import {useMemo} from 'react';

import {TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
export class Counter<T> {
  private counts: Map<string, number>;
  constructor(
    items: Array<T>,
    private hash: (item: T) => string
  ) {
    const hashedItems = items.map((item) => this.hash(item));
    this.counts = getItemCounts(hashedItems);
  }
  get(item: T): number {
    return this.counts.get(this.hash(item)) ?? 0;
  }
}

/**
 * Helper method returning a map from items in an array of strings to their counts.
 */
export function getItemCounts(items: Array<string>): Map<string, number> {
  const counts = new Map<string, number>();

  items.forEach((item) => {
    if (counts.has(item)) {
      // Increment count if item already exists
      counts.set(item, counts.get(item)! + 1);
    } else {
      // Initialize count if item does not exist
      counts.set(item, 1);
    }
  });

  return counts;
}

export function useStudyDesignItemTableSharedProps(): {allowReorderDimensions: TableReorderLocks} {
  const allowReorderDimensions = useMemo(
    () => ({
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    }),
    []
  );
  return {
    allowReorderDimensions,
  };
}
